import React, { useState } from 'react';
import { useWebRTCContext } from '../WebRTCContext';
import * as S from '../styled';

// Mic Icon
const MicIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 15c1.66 0 2.99-1.34 2.99-3L15 6c0-1.66-1.34-3-3-3S9 4.34 9 6v6c0 1.66 1.34 3 3 3zm-1.2-9.1c0-.66.54-1.2 1.2-1.2s1.2.54 1.2 1.2l-.01 6.2c0 .66-.53 1.2-1.19 1.2s-1.2-.54-1.2-1.2V5.9zm6.5 6.1c0 3-2.54 5.1-5.3 5.1S6.7 15 6.7 12H5c0 3.41 2.72 6.23 6 6.72V22h2v-3.28c3.28-.48 6-3.3 6-6.72h-1.7z"/>
  </svg>
);

// Mic Off Icon
const MicOffIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 11h-1.7c0 .74-.16 1.43-.43 2.05l1.23 1.23c.56-.98.9-2.09.9-3.28zm-4.02.17c0-.06.02-.11.02-.17V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.18l5.98 5.99zM4.27 3L3 4.27l6.01 6.01V11c0 1.66 1.33 3 2.99 3 .22 0 .44-.03.65-.08l1.66 1.66c-.71.33-1.5.52-2.31.52-2.76 0-5.3-2.1-5.3-5.1H5c0 3.41 2.72 6.23 6 6.72V21h2v-3.28c.91-.13 1.77-.45 2.54-.9L19.73 21 21 19.73 4.27 3z" />
  </svg>
);

// End Call Icon
const EndCallIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.59 10.52c1.05.51 2.04 1.15 2.96 1.91l-1.07 1.07c-.58-.47-1.21-.89-1.88-1.27v-1.71m-13.2 0v1.7c-.65.37-1.28.79-1.87 1.27l-1.07-1.07c.91-.75 1.9-1.38 2.94-1.9M12 7C7.46 7 3.34 8.78.29 11.67c-.18.18-.29.43-.29.71s.11.53.29.7l2.48 2.48c.18.18.43.29.71.29.27 0 .52-.1.7-.28.79-.73 1.68-1.36 2.66-1.85.33-.16.56-.51.56-.9v-3.1C8.85 9.25 10.4 9 12 9s3.15.25 4.59.73v3.1c0 .4.23.74.56.9.98.49 1.87 1.11 2.66 1.85.18.17.43.28.7.28.28 0 .53-.11.71-.29l2.48-2.48c.18-.18.29-.43.29-.71s-.11-.53-.29-.71C20.66 8.78 16.54 7 12 7z"/>
  </svg>
);

const AudioControls = ({ onToggleMute, onEndCall, isMuted, onRestartCall }) => {
  const { state, dispatch } = useWebRTCContext();
  const { connectionState } = state;
  // Use local state if isMuted prop is not provided
  const [localIsMuted, setLocalIsMuted] = useState(false);
  
  // Use provided isMuted prop or local state
  const muted = isMuted !== undefined ? isMuted : localIsMuted;
  
  const handleMuteToggle = () => {
    // Only update local state if isMuted prop is not provided
    if (isMuted === undefined) {
      setLocalIsMuted(!localIsMuted);
    }
    
    // Call external handler if provided
    if (onToggleMute) {
      onToggleMute();
    }
  };
  
  const handleEndCall = () => {
    // Update the connection state
    dispatch({ type: 'SET_CONNECTION_STATE', payload: 'disconnected' });
    
    // Call external handler if provided
    if (onEndCall) {
      onEndCall();
    }
  };

  const handleRestartCall = () => {
    if (onRestartCall) {
      onRestartCall();
    }
  };
  
  const getStatusText = () => {
    switch(connectionState) {
      case 'idle': return 'Ready to connect';
      case 'connecting': return 'Connecting...';
      case 'connected': return 'Connected';
      case 'disconnected': return 'Disconnected';
      case 'error': return 'Connection error';
      default: return 'Unknown state';
    }
  };
  
  return (
    <S.ControlsContainer>
      <S.StatusContainer>
        <S.StatusIndicator status={connectionState} />
        <S.StatusText>{getStatusText()}</S.StatusText>
      </S.StatusContainer>
      
      <S.ControlButtonGroup>
        {connectionState === 'connected' && (
          <>
            <S.IconButton 
              active={muted} 
              onClick={handleMuteToggle}
              title={muted ? 'Unmute' : 'Mute'}
            >
              {muted ? <MicOffIcon /> : <MicIcon />}
            </S.IconButton>
            <S.EndCallButton 
              onClick={handleEndCall}
            >
              <EndCallIcon /> End Call
            </S.EndCallButton>
          </>
        )}
        
        {connectionState === 'disconnected' && (
          <S.Button 
            primary
            onClick={handleRestartCall}
          >
            Restart Call
          </S.Button>
        )}
      </S.ControlButtonGroup>
    </S.ControlsContainer>
  );
};

export default AudioControls; 